import { useEffect, useMemo, useState } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { EventTypesDV } from "../../components";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantScope } from "../../hooks/useTenantScope";
import { BenefitsMetricsTenantScopeHeader } from "../Reporting/components/BenefitsMetricsWidgets";
import { CancellationReasonsSection } from "./CancellationReasonsSection";
import { CATALOG_CONTENT_STYLE } from "../../components/ui/CatalogCard";

type EventTaxonomyKey = "event_type" | "event_sub_type";
type DataTypeKey = EventTaxonomyKey | "cancellation_reason";

const EVENT_TAXONOMY_OPTIONS: { label: string; value: EventTaxonomyKey }[] = [
  { label: "Tipo de eventos", value: "event_type" },
  { label: "Subtipo de eventos", value: "event_sub_type" },
];

const CANCELLATION_OPTION: { label: string; value: "cancellation_reason" } = {
  label: "Motivo de cancelación",
  value: "cancellation_reason",
};

const isDataTypeKey = (value: string | null): value is DataTypeKey =>
  value === "event_type" || value === "event_sub_type" || value === "cancellation_reason";

export const DataTypesConfig = () => {
  const scope = useTenantScope();
  const isGlobalCatalog =
    !scope.loading && scope.role === "SUPER_ADMIN" && scope.tenantId === null;
  const { enabled, loading: flagLoading } = useIsFeatureFlagEnabled("admin_tipo_datos");
  const isResolvingAccess = scope.loading || (!isGlobalCatalog && flagLoading);
  const canAccessCancellationReasons = isGlobalCatalog || enabled;
  const [selectedType, setSelectedType] = useState<DataTypeKey | null>(null);

  const dataTypeOptions = useMemo(
    () =>
      canAccessCancellationReasons
        ? [...EVENT_TAXONOMY_OPTIONS, CANCELLATION_OPTION]
        : EVENT_TAXONOMY_OPTIONS,
    [canAccessCancellationReasons]
  );

  useEffect(() => {
    if (isResolvingAccess) return;
    if (selectedType === "cancellation_reason" && !canAccessCancellationReasons) {
      setSelectedType(null);
    }
  }, [isResolvingAccess, selectedType, canAccessCancellationReasons]);

  const handleTypeChange = (event: DropdownChangeEvent) => {
    setSelectedType(isDataTypeKey(event.value) ? event.value : null);
  };

  return (
    <div className="p-card" style={CATALOG_CONTENT_STYLE}>
      <div className="flex flex-column align-items-center w-full gap-2 mt-4 mb-3">
        <h1 className="text-center m-0">Gestión de Tipos</h1>
        <BenefitsMetricsTenantScopeHeader tenantId={scope.tenantId} />
      </div>
      {isResolvingAccess ? (
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <ProgressSpinner />
        </div>
      ) : (
        <>
          <div className="flex flex-column mb-4" style={{ maxWidth: "20rem", gap: 6 }}>
            <label htmlFor="dataType" className="text-600 text-sm">
              Tipos
            </label>
            <Dropdown
              inputId="dataType"
              value={selectedType}
              options={dataTypeOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="Seleccioná un tipo"
              showClear
              appendTo="self"
              className="w-full"
              onChange={handleTypeChange}
            />
          </div>
          {selectedType === "event_type" ? (
            <EventTypesDV variant="types" />
          ) : selectedType === "event_sub_type" ? (
            <EventTypesDV variant="subtypes" />
          ) : selectedType === "cancellation_reason" && canAccessCancellationReasons ? (
            <CancellationReasonsSection />
          ) : (
            <p className="text-color-secondary m-0">
              Seleccioná un tipo para ver su configuración.
            </p>
          )}
        </>
      )}
    </div>
  );
};
